import { useState } from "react";
import {
  useGetCmpEventDetailLazyQuery,
  useUpdateCmpEventMutation,
} from "../../../../graphql/types";
import {
  appendScheduleReviewNote,
  eventFormStateToInput,
  eventReturnToFormState,
} from "../eventBackendMappers";
import { formatEventScheduleParts } from "../eventDateTime";
import {
  getEventSaveErrorMessage,
  prepareEventDataForSave,
} from "../eventSaveInput";
import type { EventScheduleSubmission } from "../EventScheduleDialog";
import { normalizeEventDateTimeFields } from "../CreateEvent/wizard/wizardShared";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../utils/apolloErrorPolicy";
import { buildStaffErrorFallback, UserFacingError } from "../../../../utils/userFacingError";

export type ManageEventScheduleTarget = {
  id: string;
  title: string;
  timeZone?: string | null;
};

export type ManageEventScheduleResult = {
  message: string;
  highlights: string[];
};

const scheduleErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
});

/**
 * Manage Events > Actions > Schedule Event (Manage Events Screen.dc.html:
 * 340-369, 772-777): the Schedule Event pop-up opens over the list. Saving
 * runs the same checks and the same updateCmpEvent save as Schedule to
 * Publish in Update Event, so the result is the same as opening the event
 * and scheduling it there without edits.
 */
export const useManageEventSchedule = ({
  chamberId,
  chamberTimeZone,
}: {
  chamberId: string;
  chamberTimeZone: string;
}) => {
  const [target, setTarget] = useState<ManageEventScheduleTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadEventDetail] = useGetCmpEventDetailLazyQuery({
    fetchPolicy: "network-only",
    // A failed load shows in the Schedule Event pop-up, not on /500.
    context: scheduleErrorContext,
  });
  const [updateCmpEvent] = useUpdateCmpEventMutation({ context: scheduleErrorContext });

  const open = (event: ManageEventScheduleTarget) => {
    setError("");
    setTarget(event);
  };
  const close = () => {
    if (saving) return;
    setTarget(null);
    setError("");
  };

  /** Returns the success message, or null when the event was not scheduled. */
  const confirm = async ({
    publishAtUtcMilli,
    note,
  }: EventScheduleSubmission): Promise<ManageEventScheduleResult | null> => {
    if (!target || saving) return null;
    const name = target.title || "Untitled Event";
    setSaving(true);
    setError("");
    try {
      const detail = await loadEventDetail({ variables: { input: { chamberId, eventId: target.id } } });
      const savedEvent = detail.data?.getCmpEventDetail.event;
      if (!savedEvent) throw new UserFacingError("The event could not be loaded. Refresh the page and try again.");
      const timeZone = normalizeCmpTimeZone(savedEvent.timeZone || target.timeZone || chamberTimeZone).timeZone;
      const formEvent = normalizeEventDateTimeFields(eventReturnToFormState(savedEvent, timeZone));
      // Loaded on demand: the wizard module carries the rich text editor.
      const { getEventValidationIssuesByStep } = await import("../CreateEvent/wizard/EventWizard");
      const issues = getEventValidationIssuesByStep(formEvent, timeZone, true);
      if (issues.length) {
        throw new UserFacingError(`${name} is not ready to schedule. Click Cancel. Then go to Actions > Edit Event and fix these items: ${issues
          .map((group) => `Step ${group.step} ${group.title}: ${group.messages.join(" ")}`).join(" ")}`);
      }
      const scheduledEvent = appendScheduleReviewNote(
        { ...formEvent, publishAtUtcMilli, status: "Scheduled" },
        note,
      );
      const result = await updateCmpEvent({
        variables: {
          input: {
            chamberId,
            eventId: target.id,
            expectedUpdatedAtUtcMilli: formEvent.updatedAtUtcMilli,
            data: await prepareEventDataForSave(
              eventFormStateToInput({ event: scheduledEvent, timeZone }),
            ),
          },
        },
      });
      if (!result.data?.updateCmpEvent.eventSavedSuccessfully) {
        throw new UserFacingError("The event could not be scheduled. Refresh the page and try again.");
      }
      const { date, time } = formatEventScheduleParts(publishAtUtcMilli, timeZone);
      setTarget(null);
      return {
        message: `${name} has been scheduled to publish on ${date} at ${time}.`,
        highlights: [name, date, time],
      };
    } catch (scheduleError) {
      setError(getEventSaveErrorMessage(scheduleError, buildStaffErrorFallback(`schedule ${name}`)));
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { target, saving, error, open, close, confirm };
};
